// /api-keys — partner API-key management (SEARCH-BY-PARTNER + issue + revoke).
//
// The list endpoint requires ?partner= (FACTS PACK §3e) → this is a search screen,
// not a global list. Flow:
//   1. search a partner → table of that partner's keys (revoked keys remain, shown
//      with a status pill — verified live).
//   2. issue a new key for the current partner → the PLAINTEXT secret is shown ONCE
//      in a warning box with a copy button; it can never be retrieved again.
//   3. revoke an active key → ConfirmDialog (sensitive; server-side audited).

import { useCallback, useState } from 'react';
import { ApiError } from '../../lib/api';
import { canAdminWrite, useAuth } from '../../auth/AuthContext';
import { formatTimestampUs } from '../../lib/format';
import { EmptyState, ErrorState, LoadingState } from '../../components/StateBlock';
import { ConfirmDialog } from '../_shared/ConfirmDialog';
import { KNOWN_SCOPES, issueApiKey, listApiKeys, revokeApiKey, scopeLabel } from './apiKeysApi';
import type { ApiKey, IssuedApiKey } from './apiKeysApi';
import '../_shared/shared.css';
import './apikeys.css';

function StatusPill({ status }: { status?: string }) {
  const tone = status === 'active' ? 'good' : status === 'revoked' ? 'neutral' : 'warn';
  const label = status === 'active' ? '生效中' : status === 'revoked' ? '已撤銷' : status || '—';
  return <span className={`pill pill-${tone} pill-dot`}>{label}</span>;
}

interface ListState {
  loading: boolean;
  error: unknown;
  keys: ApiKey[] | null;
  partner: string; // the committed partner the list belongs to
}

export default function ApiKeysPage() {
  const { role } = useAuth();
  const writable = canAdminWrite(role); // cs-readonly 直連本頁時:簽發/撤銷收起,查詢仍可用
  // ---- partner search / list ----
  const [partnerInput, setPartnerInput] = useState('');
  const [list, setList] = useState<ListState>({ loading: false, error: null, keys: null, partner: '' });

  const search = useCallback(async (partner: string) => {
    const p = partner.trim();
    if (!p) return;
    setList({ loading: true, error: null, keys: null, partner: p });
    try {
      const keys = await listApiKeys(p);
      setList({ loading: false, error: null, keys, partner: p });
    } catch (e) {
      setList({ loading: false, error: e instanceof ApiError ? e : new Error('查詢失敗'), keys: null, partner: p });
    }
  }, []);

  const onSearchSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    void search(partnerInput);
  };

  // ---- issue form ----
  const [showIssue, setShowIssue] = useState(false);
  const [issuePartner, setIssuePartner] = useState('');
  const [issueCompany, setIssueCompany] = useState('');
  const [issueScopes, setIssueScopes] = useState<string[]>(['device:read']);
  const [extraScope, setExtraScope] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [issueError, setIssueError] = useState<string | null>(null);
  const [issued, setIssued] = useState<IssuedApiKey | null>(null);
  const [copied, setCopied] = useState(false);

  function openIssue() {
    setIssuePartner(list.partner || partnerInput.trim());
    setIssueCompany('');
    setIssueScopes(['device:read']);
    setExtraScope('');
    setIssueError(null);
    setIssued(null);
    setCopied(false);
    setShowIssue(true);
  }

  function toggleScope(scope: string) {
    setIssueScopes((s) => (s.includes(scope) ? s.filter((x) => x !== scope) : [...s, scope]));
  }

  const issuedScopeList = useCallback((): string[] => {
    const extra = extraScope
      .split(/[,\s]+/)
      .map((x) => x.trim())
      .filter(Boolean);
    return Array.from(new Set([...issueScopes, ...extra]));
  }, [issueScopes, extraScope]);

  async function doIssue() {
    const partner = issuePartner.trim();
    if (!partner) {
      setIssueError('請輸入合作夥伴（必填）。');
      return;
    }
    const scopes = issuedScopeList();
    if (scopes.length === 0) {
      setIssueError('請至少選擇一個 scope。');
      return;
    }
    setIssuing(true);
    setIssueError(null);
    try {
      const result = await issueApiKey({ partner, companyId: issueCompany.trim() || undefined, scopes });
      setIssued(result);
      setCopied(false);
      // refresh the list if we're viewing this partner (so the new key appears)
      if (list.partner === partner) void search(partner);
    } catch (err) {
      setIssueError(err instanceof ApiError ? `${err.message}（${err.code}）` : '簽發失敗，請稍後再試。');
    } finally {
      setIssuing(false);
    }
  }

  async function copySecret(secret: string) {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  function closeIssue() {
    setShowIssue(false);
    setIssued(null);
  }

  // ---- revoke ----
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  async function doRevoke(key: ApiKey) {
    setRevoking(true);
    setRevokeError(null);
    try {
      await revokeApiKey(key.api_key_id);
      setRevokeTarget(null);
      if (list.partner) void search(list.partner);
    } catch (e) {
      setRevokeError(e instanceof ApiError ? `${e.message}（${e.code}）` : '撤銷失敗，請稍後再試。');
    } finally {
      setRevoking(false);
    }
  }

  const keys = list.keys;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">API 金鑰</h1>
          <p className="page-subtitle">
            管理第三方／合作夥伴的 API 金鑰。金鑰清單以合作夥伴為查詢條件（非全域清單）；簽發後的明文金鑰僅顯示一次。
          </p>
        </div>
        {writable && (
          <button className="btn-primary" onClick={openIssue}>
            簽發金鑰
          </button>
        )}
      </div>

      {/* ---- partner search ---- */}
      <form className="toolbar-panel apikeys-search" onSubmit={onSearchSubmit} role="search">
        <div className="adm-search-row">
          <input
            type="search"
            value={partnerInput}
            onChange={(e) => setPartnerInput(e.target.value)}
            placeholder="輸入合作夥伴名稱以查詢其金鑰…"
            aria-label="以合作夥伴查詢 API 金鑰"
          />
          <button type="submit" className="btn-primary" disabled={!partnerInput.trim() || list.loading}>
            {list.loading ? '查詢中…' : '查詢'}
          </button>
        </div>
        <p className="adm-hint">
          <span aria-hidden="true">ⓘ</span>
          後端要求必須指定合作夥伴;請輸入合作夥伴代號後查詢。已撤銷的金鑰仍會列出（狀態標示為「已撤銷」）。
        </p>
      </form>

      {/* ---- list states ---- */}
      {!list.partner && !list.loading && (
        <EmptyState icon="🔑" title="輸入合作夥伴以查詢金鑰" detail="此頁不提供全域金鑰清單；請先輸入合作夥伴代號。若要為新合作夥伴簽發金鑰，按右上「簽發金鑰」。" />
      )}

      {list.loading && keys === null && <LoadingState label={`查詢 ${list.partner} 的金鑰…`} />}

      {!list.loading && list.error != null && <ErrorState error={list.error} onRetry={() => void search(list.partner)} />}

      {list.error == null && keys && keys.length === 0 && (
        <EmptyState
          icon="🗝️"
          title={`合作夥伴「${list.partner}」沒有金鑰`}
          detail="這個合作夥伴目前沒有任何 API 金鑰。可按右上「簽發金鑰」為其建立第一把。"
          action={
            writable ? (
              <button className="btn-primary btn-sm" onClick={openIssue}>
                為 {list.partner} 簽發金鑰
              </button>
            ) : undefined
          }
        />
      )}

      {list.error == null && keys && keys.length > 0 && (
        <>
          <div className="section-head">
            <h2 className="section-title">
              合作夥伴
              <span className="adm-mono text-brand apikeys-partner-name">{list.partner}</span>
            </h2>
            <span className="section-meta">
              {keys.length} 把金鑰 · {keys.filter((k) => k.status === 'active').length} 把生效中
            </span>
          </div>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>金鑰 ID</th>
                  <th>公司</th>
                  <th>權限範圍</th>
                  <th>狀態</th>
                  <th>建立時間</th>
                  <th aria-label="操作" />
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.api_key_id}>
                    <td className="adm-cell-strong adm-mono">{k.api_key_id}</td>
                    <td>{k.company_id || '—'}</td>
                    <td>
                      {k.scopes && k.scopes.length > 0 ? (
                        <div className="adm-chips">
                          {k.scopes.map((s) => (
                            <span key={s} className="adm-chip" title={scopeLabel(s)}>
                              {s}
                            </span>
                          ))}
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <StatusPill status={k.status} />
                    </td>
                    <td className="adm-mono">{formatTimestampUs(k.created_at, { second: undefined })}</td>
                    <td className="apikeys-actions-cell">
                      {k.status === 'active' ? (
                        writable ? (
                          <button
                            className="btn-ghost-danger btn-sm"
                            onClick={() => {
                              setRevokeError(null);
                              setRevokeTarget(k);
                            }}
                          >
                            撤銷
                          </button>
                        ) : (
                          <span className="text-tertiary">—</span>
                        )
                      ) : (
                        <span className="text-tertiary apikeys-revoked-note">已撤銷</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ---- issue modal:表單狀態走共用 ConfirmDialog(取消 / 簽發)---- */}
      {showIssue && !issued && (
        <ConfirmDialog
          title="簽發 API 金鑰"
          tone="primary"
          confirmLabel="簽發"
          busy={issuing}
          error={issueError}
          onConfirm={() => void doIssue()}
          onCancel={closeIssue}
          body={
            <div className="apikeys-issue-fields">
              <label className="adm-field">
                <span className="adm-field-label text-secondary">合作夥伴（必填）</span>
                <input value={issuePartner} onChange={(e) => setIssuePartner(e.target.value)} placeholder="合作夥伴代號" disabled={issuing} />
              </label>
              <label className="adm-field">
                <span className="adm-field-label text-secondary">公司 ID（選填）</span>
                <input value={issueCompany} onChange={(e) => setIssueCompany(e.target.value)} placeholder="company_id" disabled={issuing} />
              </label>
              <div className="adm-field">
                <span className="adm-field-label text-secondary">權限範圍</span>
                <div className="apikeys-scope-list">
                  {KNOWN_SCOPES.map((s) => (
                    <label key={s.value} className="apikeys-scope-opt">
                      <input type="checkbox" checked={issueScopes.includes(s.value)} onChange={() => toggleScope(s.value)} disabled={issuing} />
                      <span className="adm-mono">{s.label}</span>
                    </label>
                  ))}
                </div>
                <input
                  className="apikeys-extra-scope"
                  value={extraScope}
                  onChange={(e) => setExtraScope(e.target.value)}
                  placeholder="其他權限範圍（以逗號或空白分隔，選填）"
                  disabled={issuing}
                />
              </div>
            </div>
          }
        />
      )}

      {/* ---- issue modal:明文金鑰一次性揭露(沿用 .adm-modal 骨架,與 ConfirmDialog 視覺一致)---- */}
      {showIssue && issued && (
        <div className="adm-modal-scrim" role="presentation" onClick={closeIssue}>
          <div className="adm-modal card" role="dialog" aria-modal="true" aria-label="金鑰已簽發" onClick={(e) => e.stopPropagation()}>
            <h2 className="adm-modal-title">金鑰已簽發</h2>
            <div className="adm-secret-box adm-secret-box--alert">
              <p className="adm-secret-warn">⚠ 這是明文金鑰，僅顯示這一次。請立即複製並交付合作夥伴；關閉後將無法再次取得。</p>
              <div className="adm-secret-row">
                <code className="adm-secret-value">{issued.plaintext || '（後端未回傳明文金鑰）'}</code>
                {issued.plaintext && (
                  <button className="btn-ghost" onClick={() => void copySecret(issued.plaintext)}>
                    {copied ? '已複製' : '複製'}
                  </button>
                )}
              </div>
            </div>
            <dl className="adm-kv">
              <dt>金鑰 ID</dt>
              <dd className="adm-mono">{issued.key.api_key_id || '—'}</dd>
              <dt>合作夥伴</dt>
              <dd>{issued.key.partner}</dd>
              {issued.key.company_id && (
                <>
                  <dt>公司</dt>
                  <dd>{issued.key.company_id}</dd>
                </>
              )}
              <dt>權限範圍</dt>
              <dd>
                {issued.key.scopes && issued.key.scopes.length > 0 ? (
                  <div className="adm-chips">
                    {issued.key.scopes.map((s) => (
                      <span key={s} className="adm-chip">
                        {s}
                      </span>
                    ))}
                  </div>
                ) : (
                  '—'
                )}
              </dd>
            </dl>
            <div className="adm-modal-actions">
              <button className="btn-primary" onClick={closeIssue}>
                我已安全保存，關閉
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- revoke confirm ---- */}
      {revokeTarget && (
        <ConfirmDialog
          title="撤銷此 API 金鑰？"
          tone="danger"
          warning="撤銷後此金鑰立即失效且無法復原，使用它的合作夥伴整合將停止運作。"
          confirmLabel="確認撤銷"
          busy={revoking}
          error={revokeError}
          onConfirm={() => void doRevoke(revokeTarget)}
          onCancel={() => {
            if (!revoking) {
              setRevokeTarget(null);
              setRevokeError(null);
            }
          }}
          body={
            <div className="apikeys-confirm-body">
              <p>即將撤銷以下金鑰，此操作會被稽核記錄：</p>
              <dl className="adm-kv">
                <dt>金鑰 ID</dt>
                <dd className="adm-mono">{revokeTarget.api_key_id}</dd>
                <dt>合作夥伴</dt>
                <dd>{revokeTarget.partner}</dd>
              </dl>
            </div>
          }
        />
      )}
    </>
  );
}
